import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Shield, Lock, Eye, Database, ArrowRight, CheckCircle } from 'lucide-react';

const PrivacyPage = () => {
  const dataTypes = [
    {
      icon: Database,
      title: "Données d'Identité",
      description: "Nom, prénom, adresse, téléphone, email",
      usage: "Gestion des réservations et communication"
    },
    {
      icon: Lock,
      title: "Données Financières",
      description: "Informations de paiement (cryptées)",
      usage: "Traitement sécurisé des transactions"
    },
    {
      icon: Eye,
      title: "Données de Navigation",
      description: "Cookies, préférences, historique de visite",
      usage: "Amélioration de l'expérience utilisateur"
    }
  ];

  const rights = [
    "Droit d'accès à vos données personnelles",
    "Droit de rectification des informations inexactes",
    "Droit à l'effacement (droit à l'oubli)",
    "Droit à la portabilité de vos données",
    "Droit d'opposition au traitement",
    "Droit à la limitation du traitement"
  ];

  const securityMeasures = [
    "Chiffrement SSL/TLS pour toutes les transmissions",
    "Serveurs sécurisés dans des centres de données certifiés",
    "Accès aux données strictement limité et contrôlé",
    "Audits de sécurité réguliers par des experts externes",
    "Sauvegarde automatique et redondance des données",
    "Formation continue du personnel sur la sécurité"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge 
            variant="outline" 
            className="mb-6 px-6 py-3 bg-white/50 backdrop-blur-sm border-gray-200 text-gray-700 text-sm font-light uppercase tracking-wider"
          >
            <Shield className="w-4 h-4 mr-2" />
            Confidentialité & RGPD
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Politique de Confidentialité
          </h1>
          
          <p 
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Votre vie privée est précieuse. Découvrez comment PRIVIA protège et utilise vos données personnelles
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium text-sm">Conforme RGPD</span>
            </div>
          </div>
        </div>
      </section>

      {/* Data Collection */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Quelles Données Collectons-nous ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Nous ne collectons que les données strictement nécessaires pour vous offrir le meilleur service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {dataTypes.map((dataType, index) => {
              const IconComponent = dataType.icon;
              return (
                <Card key={index} className="p-6 text-center hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-blue-600" />
                  </div>
                  
                  <h3 className="text-lg font-medium text-gray-800 mb-3">
                    {dataType.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4">
                    {dataType.description}
                  </p>
                  
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Usage</p>
                    <p className="text-xs text-gray-700">{dataType.usage}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Your Rights */}
      <section className="py-16 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 
                className="text-3xl font-light mb-8 text-gray-800"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Vos Droits RGPD
              </h2>
              
              <div className="space-y-4">
                {rights.map((right, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{right}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Button 
                  variant="outline"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-6 py-3 rounded-full font-medium transition-all duration-300"
                >
                  Exercer mes Droits
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            <div>
              <h2 
                className="text-3xl font-light mb-8 text-gray-800"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Sécurité des Données
              </h2>
              
              <div className="space-y-4">
                {securityMeasures.map((measure, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{measure}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Processing */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8">
            <h2 
              className="text-2xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Bases Légales du Traitement
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-800 mb-3">Exécution du Contrat</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Traitement nécessaire pour la gestion de vos réservations, 
                  la fourniture de nos services et la communication liée à votre séjour.
                </p>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-800 mb-3">Intérêt Légitime</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Amélioration de nos services, prévention de la fraude, 
                  et communication marketing (avec possibilité d'opposition).
                </p>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-800 mb-3">Consentement</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Cookies non essentiels, newsletters, et partage de données 
                  avec nos partenaires pour des offres personnalisées.
                </p>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-800 mb-3">Obligation Légale</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Conservation de certaines données pour répondre aux 
                  obligations comptables, fiscales et réglementaires.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Contact DPO */}
      <section className="py-16 px-6 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Délégué à la Protection des Données
          </h2>
          <p 
            className="text-lg mb-8 text-gray-600 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Pour toute question concernant le traitement de vos données personnelles, 
            contactez notre DPO dédié
          </p>
          
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto mb-8">
            <p className="text-sm text-gray-600 mb-2">Email DPO</p>
            <p className="text-lg font-medium text-gray-800">dpo@priviacollection.com</p>
          </div>

          <Button 
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Contacter le DPO
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;